import React, { createContext, useContext, useState, useEffect } from 'react'
import api from '../api'

interface User {
  id: string
  email: string
  display_name: string
  organisation_id: string
  role: string
  plan_tier: string
  is_verified: boolean
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  register: (email: string, password: string, displayName: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('velumio_token')
    const savedUser = localStorage.getItem('velumio_user')

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser))
      } catch {
        localStorage.removeItem('velumio_token')
        localStorage.removeItem('velumio_user')
      }
    }

    setLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    const res = await api.post('/auth/login', { email, password })
    const { token, user: userData } = res.data
    localStorage.setItem('velumio_token', token)
    localStorage.setItem('velumio_user', JSON.stringify(userData))
    setUser(userData)
  }

  const register = async (
    email: string,
    password: string,
    displayName: string
  ) => {
    // Register but do NOT log the user in
    // They must verify their email first
    await api.post('/auth/register', {
      email,
      password,
      display_name: displayName,
    })
    // Do not store token or set user here
    // Caller handles redirect to check-email page
  }

  const logout = () => {
    localStorage.removeItem('velumio_token')
    localStorage.removeItem('velumio_user')
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, register }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}